import React, { useState, useEffect, useRef, useMemo } from "react";
import { Container, Button, Progress, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import tabStyles from "../../PrintLabel/MainPrintListTabs/css/labeltypetabs.module.scss";
import ac from "../css/scanning.module.scss";
import resStyles from "../css/scanningSessionResponsive.module.scss";
import SubjectToVacutainer from "../components/SubjectToVacutainerScanning";
import VacutainerToAliquots from "../components/VacutainerToAliquotsScanning";
import MobileHeader from "../components/MobileScanningHeader";
import MobileScansOverlay from "../components/MobileScanningScansOverlay";
import MobileStep1Scanner from "../components/MobileScanningStep1Scanner";
import MobileStep2Scanner from "../components/MobileScanningStep2Scanner";
import { useSampleCollectionTimeService } from "modules/ScanningApp/hooks/useSampleCollectionTime";
import {
  useScanningSessionService,
  useScanningScanSampleCollectionService,
  useScanningScanSampleSeperationService,
} from "../api/useScanningSession";
import {
  CurrentUser,
  GetUserData,
  showToastMessage,
  GetSystemSettingValue,
} from "common/service/CommonService";
import { ACCESSIONING_PATHS, SCANNING_PATHS } from "Routes/paths";
import { queryClient } from "api/queryClient";
import FullPageLoader from "Components/Common/FullPageLoader";
import ScanningSessionProgressModal from "../components/ScanningSessionProgressModal";
import SkipReasonModal from "../components/SkipReasonModal";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import RouteGuard from "Routes/RouteGuard";
import { usePermission } from "context/PermissionContext";

/**
 * Plays a high-pitched success beep sound (1800Hz square wave, 180ms)
 * to provide immediate acoustic feedback upon successful barcode match/scan.
 */
const playSuccessBeep = async () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "square";
    oscillator.frequency.setValueAtTime(1800, audioContext.currentTime);

    gainNode.gain.setValueAtTime(0.9, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.18);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.18);
  } catch (error) {
    console.error("Beep sound error:", error);
  }
};

/**
 * Plays a low-pitched warning/error beep sound (220Hz sawtooth wave, 350ms)
 * to alert the user in case of barcode validation failure or mismatch.
 */
const playErrorBeep = async () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "sawtooth";
    oscillator.frequency.setValueAtTime(220, audioContext.currentTime);

    gainNode.gain.setValueAtTime(0.9, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.35);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.35);
  } catch (error) {
    console.error("Beep sound error:", error);
  }
};

/** Active Tab Key Type definition */
type AccessioningTabKey = "select" | "step1" | "step2";

/**
 * ScanningSessionPage Component
 * 
 * Main workflow page for executing clinical scanning sessions.
 * Supports both:
 * - Step 1: Subject -> Vacutainer barcode scanning & sample collection time recording.
 * - Step 2: Vacutainer -> Aliquots sample separation scanning.
 * 
 * Includes responsive layout handling (Desktop vs Mobile scanner views),
 * continuous camera barcode detection via Web API, audio/speech feedback,
 * and performance-optimized React Query fetching.
 */
const ScanningSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Read system setting to determine if sample collection capture mode is Auto or Manual
  const sampleCollectionMode =
    GetSystemSettingValue("Sample Collection Mode") ||
    GetSystemSettingValue("Collection Capture Mode") ||
    GetSystemSettingValue("Collection Mode") ||
    "Auto";
  const isAutoCollect = sampleCollectionMode.toLowerCase() === "auto";

  // Concurrency & Double-Scan Lock Refs (Prevents duplicate processing on rapid camera frames)
  const scanLockRef = useRef(false);
  const lastScannedRef = useRef("");
  const isProcessingRef = useRef(false);
  const [isProcessing, setIsProcessingState] = useState(false);

  /**
   * Updates state & ref tracking in-flight API calls to block extra camera scans while processing.
   */
  const setProcessing = (val: boolean) => {
    isProcessingRef.current = val;
    setIsProcessingState(val);
  };

  // Responsive Layout Tracking (Desktop > 1024px vs Mobile <= 1024px)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isDesktop = windowWidth > 1024;

  // Determine parent route module path prefix (Scanning vs Accessioning)
  const isScanning = location.pathname.startsWith("/scanning");
  const sessionPathPattern = isScanning ? SCANNING_PATHS.SESSION : ACCESSIONING_PATHS.SESSION;
  const listPath = isScanning ? SCANNING_PATHS.VIEW : ACCESSIONING_PATHS.LIST;

  // Tab & Dialog UI States
  const [activeTab, setActiveTab] = useState<AccessioningTabKey>("step1");
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const isManualAction = useRef(false);

  // Performance timer reference for scan latency telemetry
  const scanStartTimeRef = useRef<number>(performance.now());
  const resetScanStartTime = () => {
    scanStartTimeRef.current = performance.now();
  };

  // User Permissions
  const { hasPermission } = usePermission();
  const permission1 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER);
  const permission2 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT);

  /**
   * Synchronize active tab from URL path segment (e.g. /step2 -> Step 2 tab)
   * also respecting user role permissions.
   */
  /**
   * Synchronize active tab from URL path segment (e.g. /step2 -> Step 2 tab)
   */
  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];

    if (last === "step2") {
      setActiveTab("step2");
    } else {
      setActiveTab("step1");
    }
  }, [location.pathname]);

  /**
   * Switches active workflow tab and updates browser location path.
   */
  const toggleTab = (tabKey: AccessioningTabKey): void => {
    const basePath = sessionPathPattern.replace(":id", id || "");
    if (isDesktop) {
      navigate(tabKey === "step1" ? basePath : `${basePath}/${tabKey}`);
    } else {
      if (tabKey === "select") {
        navigate(basePath);
      } else {
        navigate(`${basePath}/${tabKey}`);
      }
    }
    setActiveTab(tabKey);
  };

  // Current logged in user context
  const user = GetUserData() as CurrentUser;

  // Session Status Update Mutation Hook
  const { UpdateMutation } = useScanningSessionService({
    sessionid: id,
  });

  const parsedSessionId = id ? parseInt(id, 10) : undefined;
  const [overlaySearchText, setOverlaySearchText] = useState("");
  const [overlayCurrentPage, setOverlayCurrentPage] = useState(1);
  const [overlayTotalCount, setOverlayTotalCount] = useState(0);
  const [isShowingAllScans, setIsShowingAllScans] = useState(false);

  // Reset overlay search and pagination when switching tabs
  useEffect(() => {
    setOverlayCurrentPage(1);
    setOverlaySearchText("");
  }, [activeTab]);

  // Reset overlay pagination when opening scans overlay
  useEffect(() => {
    if (isShowingAllScans) {
      setOverlayCurrentPage(1);
    }
  }, [isShowingAllScans]);

  // Clamp current page if total count changes and current page is out of bounds
  useEffect(() => {
    const totalPages = Math.ceil(overlayTotalCount / 10) || 1;
    if (overlayCurrentPage > totalPages) {
      setOverlayCurrentPage(totalPages);
    }
  }, [overlayTotalCount, overlayCurrentPage]);

  /**
   * PERFORMANCE OPTIMIZATION:
   * SampleCollection queries (Session/Select & SelectAll) are enabled ONLY on Step 1 (`activeTab === "step1"`).
   * SampleSeperation queries (Session/Select & SelectAll) are enabled ONLY on Step 2 (`activeTab === "step2"`).
   * Each active tab triggers its respective queries exactly ONCE, preventing dual/duplicate polling.
   */
  const {
    SampleCollectionSessionSelectQuery: sampleCollectionSessionSelect,
    SelectAllQuery: collectionSelectAll,
  } = useScanningScanSampleCollectionService({
    sessionid: parsedSessionId,
    searchtext: overlaySearchText,
    currentpage: overlayCurrentPage,
    pagesize: 10,
    isSessionSelectEnabled: !!parsedSessionId && activeTab === "step1",
    isSelectAllEnabled: !!parsedSessionId && activeTab === "step1",
  });

  const {
    SampleSeperationSessionSelectQuery: sampleSeperationSessionSelect,
    SelectAllQuery: separationSelectAll,
  } = useScanningScanSampleSeperationService({
    sessionid: parsedSessionId,
    searchtext: overlaySearchText,
    currentpage: overlayCurrentPage,
    pagesize: 10,
    isSessionSelectEnabled: !!parsedSessionId && activeTab === "step2",
    isSelectAllEnabled: !!parsedSessionId && activeTab === "step2",
  });

  // Active query pointers based on active tab
  const activeSessionQuery =
    activeTab === "step2" ? sampleSeperationSessionSelect : sampleCollectionSessionSelect;
  const activeSelectAllQuery =
    activeTab === "step2" ? separationSelectAll : collectionSelectAll;

  // Track total records count independently so empty pages do not collapse the pagination bar
  useEffect(() => {
    const rawData = activeSelectAllQuery?.data?.data || [];
    if (rawData.length > 0) {
      const backendTotal = rawData[0]?.total_count;
      if (backendTotal !== undefined && backendTotal !== null) {
        setOverlayTotalCount(backendTotal);
      }
    } else if (overlayCurrentPage === 1) {
      setOverlayTotalCount(0);
    }
  }, [activeSelectAllQuery?.data?.data, overlayCurrentPage]);

  const progressItems = activeSessionQuery?.data?.data || [];
  const firstItem = progressItems[0] || null;
  const sessionData = firstItem;

  // Total subjects count in session
  const totalSubs =
    activeTab === "step2"
      ? firstItem?.totalsubjects ?? 0
      : firstItem?.total_count ?? firstItem?.totalsubjects ?? 0;

  /**
   * Computes completed vs in-progress subject counts based on step activity type.
   */
  const { completedSubsCount, inProgressSubsCount } = useMemo(() => {
    const isSeparation = activeTab === "step2" || sessionData?.sessionactivity === "sample_separation";
    if (isSeparation) {
      const completedAliq = sessionData?.completedaliquots || 0;
      const aliqPerSub = sessionData?.aliquotspersubject || 0;
      const calcCompleted = Math.floor(completedAliq / Math.max(aliqPerSub, 1));
      return {
        completedSubsCount: calcCompleted,
        inProgressSubsCount: 0,
      };
    } else {
      if (sessionData?.completedsubjects !== undefined) {
        const compSub = (sessionData?.completedsubjects || 0) + (sessionData?.skippedsubjects || 0);
        return {
          completedSubsCount: compSub,
          inProgressSubsCount: 0,
        };
      }
      let completed = 0;
      let inProgress = 0;
      progressItems.forEach((item: any) => {
        const overallStatus = item.overall_status?.toUpperCase();
        const vacStatus = item.vaccutainer_status?.toUpperCase();
        const isSkipped = overallStatus === "SKIPPED" || vacStatus === "SKIPPED";

        if (isSkipped || overallStatus === "COMPLETED" || vacStatus === "COMPLETED") {
          completed++;
        } else if (vacStatus === "IN_PROGRESS" || overallStatus === "IN_PROGRESS") {
          inProgress++;
        }
      });

      return {
        completedSubsCount: completed,
        inProgressSubsCount: inProgress,
      };
    }
  }, [progressItems, activeTab, sessionData]);

  const completedSubs = completedSubsCount;
  const inProgressSubs = inProgressSubsCount;

  /**
   * Determines if the session meets requirements to enable the "Complete Session" action button.
   */
  const isSessionCompletedReady = useMemo(() => {
    if (!totalSubs || totalSubs <= 0) return false;

    const isSeparation = activeTab === "step2" || sessionData?.sessionactivity === "sample_separation";

    if (isSeparation) {
      const completedAliq = sessionData?.completedaliquots || 0;
      const aliqPerSub = sessionData?.aliquotspersubject || 0;
      const skippedSubs = sessionData?.skippedsubjects || 0;

      const completedFromAliq = Math.floor(completedAliq / Math.max(aliqPerSub, 1));

      return completedFromAliq >= totalSubs || completedFromAliq + skippedSubs >= totalSubs;
    } else {
      const rawCompletedSubs = sessionData?.completedsubjects || 0;
      const rawSkippedSubs = sessionData?.skippedsubjects || 0;

      return rawCompletedSubs >= totalSubs || rawCompletedSubs + rawSkippedSubs >= totalSubs;
    }
  }, [totalSubs, activeTab, sessionData]);

  /**
   * Updates session status ('active', 'paused', 'completed') and invalidates query caches.
   */
  const handleUpdateStatus = (
    status: "paused" | "completed" | "active",
    shouldNavigate = true,
  ) => {
    if (shouldNavigate) isManualAction.current = true;
    const payload = {
      sessionid: id ? parseInt(id) : 1,
      sessionstate: status,
      updatereason: "Update Session Status",
    };

    UpdateMutation.mutate(payload, {
      onError: (err: any) => {
        showToastMessage("error", err || `Failed to ${status} session`);
      },
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["sample-seperation-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
        if (shouldNavigate) {
          showToastMessage("success", res?.message || `Session ${status} successfully`);
          const currentCategory =
            activeTab === "step2" || sessionData?.sessionactivity === "sample_separation"
              ? "sample_separation"
              : "sample_collection";
          navigate(listPath, {
            replace: true,
            state: { viewingSessionId: id, openForm: true, sessionCategory: currentCategory },
          });
        } else {
          showToastMessage("success", res?.message || `Session ${status} successfully`);
        }
      },
    });
  };

  const isSessionCompleted =
    String(sessionData?.sessionstate || "").toLowerCase() === "completed";

  const handleBackClick = () => {
    if (isSessionCompleted) {
      isManualAction.current = true;
      const currentCategory =
        activeTab === "step2" || sessionData?.sessionactivity === "sample_separation"
          ? "sample_separation"
          : "sample_collection";
      navigate(listPath, {
        replace: true,
        state: { viewingSessionId: id, openForm: true, sessionCategory: currentCategory },
      });
    } else {
      handleUpdateStatus("paused", true);
    }
  };

  const sessionDisplayStatus =
    sessionData?.display_status || (sessionData?.sessionstate ? sessionData.sessionstate.toUpperCase() : "PAUSED");

  const displayStatusRef = useRef(sessionDisplayStatus);
  useEffect(() => {
    displayStatusRef.current = sessionDisplayStatus;
  }, [sessionDisplayStatus]);

  /**
   * Auto-pause session when user navigates away without manually setting status.
   */
  useEffect(() => {
    return () => {
      const currentStatus = String(displayStatusRef.current || "").toUpperCase();
      if (!isManualAction.current && currentStatus !== "PAUSED" && currentStatus !== "COMPLETED") {
        handleUpdateStatus("paused", false);
      }
    };
  }, []);

  // Barcode state tracking
  const [subjectBarcode, setSubjectBarcode] = useState("");
  const [vacutainerBarcode, setVacutainerBarcode] = useState("");
  const [subjectStatus, setSubjectStatus] = useState<"idle" | "matched" | "mismatch">("idle");
  const [vacutainerStatus, setVacutainerStatus] = useState<"idle" | "matched" | "mismatch">("idle");
  const [validatedSubjectData, setValidatedSubjectData] = useState<any>(null);

  // Sample Collection confirmation states
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [collectTime, setCollectTime] = useState("");
  const [isCollectScanning, setIsCollectScanning] = useState(false);

  // Step 2 Aliquot scanning states
  const [activeAliquotIndex, setActiveAliquotIndex] = useState(0);
  const [aliquotStatus, setAliquotStatus] = useState<"idle" | "matched" | "mismatch">("idle");
  const [aliquotBarcodes, setAliquotBarcodes] = useState<string[]>([]);

  // Toast / Status form message
  const [formMessage, setFormMessage] = useState<{
    text: string;
    type: "success" | "error" | "";
  }>({ text: "", type: "" });

  /**
   * API Services for Scans - Scoped query execution to active Tab
   */
  const collectionService = useScanningScanSampleCollectionService({
    sessionid: id ? parseInt(id) : sessionData?.sessionid || 0,
    searchtext: "",
    currentpage: 1,
    pagesize: 10,
    isSessionSelectEnabled: false,
    isSelectAllEnabled: false,
  });

  const separationService = useScanningScanSampleSeperationService({
    sessionid: id ? parseInt(id) : sessionData?.sessionid || 0,
    searchtext: "",
    currentpage: 1,
    pagesize: 10,
    isSessionSelectEnabled: false,
    isSelectAllEnabled: false,
  });

  const { SelectAllQuery: sampleCollectionTimeQuery, UpdateMutation: CollectionTimeUpdateMutation } =
    useSampleCollectionTimeService({
      studyid: sessionData?.studyid,
      period_number: sessionData?.period_number,
      timepoint_number: sessionData?.timepoint_number,
      filterstatus: "ALL",
      enabled: activeTab === "step1" && !!sessionData?.studyid && !!sessionData?.period_number && !!sessionData?.timepoint_number,
    });

  /** Formats current time as HH:mm:ss for backend updates */
  const getNow = (): string => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  /** Formats current time for modal display */
  const getNowDisplay = (): string => {
    const now = new Date();
    return now.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  /**
   * Handles "Collect Sample" button click:
   * Opens secondary vacutainer re-scan window if Auto Mode, or opens Confirm Modal if Manual Mode.
   */
  const handleCollectClick = () => {
    scanLockRef.current = false;
    lastScannedRef.current = "";
    if (isAutoCollect) {
      setIsCollectScanning(true);
      setFormMessage({ text: "", type: "" });
    } else {
      setCollectTime(getNowDisplay());
      setShowCollectModal(true);
    }
  };

  /**
   * Submits sample collection record and updates collection timestamp in database.
   */
  const handleConfirmCollect = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    collectionService.InsertMutation.mutate(
      {
        sessionid: id ? parseInt(id) : sessionData?.sessionid,
        subjectbarcode: subjectBarcode,
        vaccutainerbarcode: vacutainerBarcode,
        result: "valid",
        resultmessage: "Match & Sample Collected Successfully",
        isverify: false,
        isinsert: true,
      },
      {
        onSuccess: () => {
          const payload = {
            vaccutainerbarcode: vacutainerBarcode,
            samplecollectiontime: getNow(),
            updatedby: Number(user?.userid),
            updatereason: "Sample collection recorded",
            studyid: sessionData?.studyid,
          };

          CollectionTimeUpdateMutation.mutate(payload, {
            onSuccess: (res: any) => {
              showToastMessage("success", res?.message || "Sample collection time updated successfully.");
              setShowCollectModal(false);
              playSuccessBeep();
              speak("Sample collection recorded.");
              queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
              queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              setProcessing(false);
              clearAndRestart();
            },
            onError: (err: any) => {
              showToastMessage("error", err?.message || err || "Failed to update sample collection time.");
              setProcessing(false);
              setShowCollectModal(false);
              clearAndRestart();
            },
          });
        },
        onError: (err: any) => {
          const errorData = err?.response?.data || err;
          const msg = typeof errorData === "string" ? errorData : errorData?.message || "Failed to record sample collection.";
          showToastMessage("error", msg);
          setProcessing(false);
          setShowCollectModal(false);
          clearAndRestart();
        },
      }
    );
  };

  /**
   * Generates synthetic vacutainer barcode string when skipping subject sample collection.
   */
  const generateVacutainerBarcode = (subBarcode: string, session: any): string => {
    if (!subBarcode || subBarcode.length < 6) return subBarcode || "";
    const prefix = subBarcode.substring(0, 6);
    const period = session?.period_sort ?? session?.period_number ?? 1;
    const tpNum = Number(session?.timepoint_sort ?? session?.timepoint_number ?? 1);
    const timepoint = tpNum < 10 ? `0${tpNum}` : `${tpNum}`;
    return `${prefix}${period}${timepoint}1`;
  };

  /** Opens skip reason modal */
  const handleSkipSubject = () => {
    if (!subjectBarcode && subjectStatus !== "matched") {
      showToastMessage("error", "Please scan subject barcode before skipping.");
      return;
    }
    setSkipReason("");
    setShowSkipModal(true);
  };

  /** Submits skipped subject record with user reason */
  const handleConfirmSkipSubject = (reasonText: string) => {
    if (isProcessingRef.current) return;
    const targetVacutainer = vacutainerBarcode || generateVacutainerBarcode(subjectBarcode, sessionData);
    const formattedReason = reasonText.trim();
    const currentUser = GetUserData();

    setProcessing(true);

    const payload = {
      sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
      subjectbarcode: subjectBarcode,
      vaccutainerbarcode: targetVacutainer,
      result: "skipped",
      resultmessage: "Subject Sample Collection Skipped",
      skipreason: formattedReason,
      scanby: currentUser?.userid || 1,
      isverify: false,
      isinsert: true,
    };

    collectionService.InsertMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Subject skipped successfully.");
        speak("Subject Skipped.");
        playSuccessBeep();
        queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
        queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
        setShowSkipModal(false);
        setSkipReason("");
        setProcessing(false);
        clearAndRestart();
      },
      onError: (err: any) => {
        const errorData = err?.response?.data || err;
        const msg = typeof errorData === "string" ? errorData : errorData?.message || "Failed to skip subject.";
        showToastMessage("error", msg);
        speak("Failed to skip subject.");
        playErrorBeep();
        setProcessing(false);
      },
    });
  };

  // Live clock tick interval for confirm collection modal
  useEffect(() => {
    if (!showCollectModal) return;
    setCollectTime(getNowDisplay());
    const intervalId = setInterval(() => {
      setCollectTime(getNowDisplay());
    }, 1000);
    return () => clearInterval(intervalId);
  }, [showCollectModal]);

  const subjectCollections = useMemo(() => {
    return (sampleCollectionTimeQuery?.data?.data || []).filter((row: any) => !row.summarycard);
  }, [sampleCollectionTimeQuery?.data?.data]);

  /** Recent scan logs list populated according to active tab */
  const historyScans = useMemo(() => {
    if (activeTab === "step1") {
      const collectionScans = collectionSelectAll?.data?.data || collectionService.SelectAllQuery?.data?.data || [];
      const mapped = collectionScans.map((scan: any) => {
        const matched = subjectCollections.find(
          (sc: any) => sc.barcode === scan.subjectbarcode
        );
        return {
          ...scan,
          dosingscheduletime: matched?.dosingscheduletime || null,
          samplecollectiontime: matched?.samplecollectiontime || null,
        };
      });
      return mapped;
    } else {
      const separationScans = separationSelectAll?.data?.data || separationService.SelectAllQuery?.data?.data || [];
      return [...separationScans];
    }
  }, [activeTab, collectionSelectAll?.data?.data, separationSelectAll?.data?.data, collectionService.SelectAllQuery?.data?.data, separationService.SelectAllQuery?.data?.data, subjectCollections]);

  // Synchronize manual aliquot barcode array size when aliquotspersubject changes
  useEffect(() => {
    if (sessionData?.aliquotspersubject) {
      setAliquotBarcodes(Array(sessionData?.aliquotspersubject).fill(""));
    }
  }, [sessionData?.aliquotspersubject]);

  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechTimeoutRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }
      if (activeUtteranceRef.current && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
      }
    };
  }, []);

  const isSpeakingRef = useRef(false);

  /**
   * Speaks voice confirmation alert via browser SpeechSynthesis API.
   */
  const speak = (msg: string, onEndCallback?: () => void) => {
    if (!("speechSynthesis" in window)) {
      onEndCallback?.();
      return;
    }
    const synth = window.speechSynthesis;

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    if (synth.speaking || synth.pending) {
      synth.cancel();
    }

    isSpeakingRef.current = true;

    speechTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      try {
        const utterance = new SpeechSynthesisUtterance(msg);
        utterance.rate = 1.0;
        utterance.lang = "en-US";

        activeUtteranceRef.current = utterance;

        const safetyTimeout = setTimeout(() => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        }, 4000);

        const cleanUp = () => {
          clearTimeout(safetyTimeout);
          isSpeakingRef.current = false;
          activeUtteranceRef.current = null;
        };

        utterance.onend = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        utterance.onerror = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        synth.speak(utterance);
      } catch (err) {
        console.error("SpeechSynthesis error:", err);
        isSpeakingRef.current = false;
        if (isMountedRef.current) {
          onEndCallback?.();
        }
      }
    }, 150);
  };

  /** Resets scanning state variables for next scan cycle */
  const clearAndRestart = () => {
    setSubjectBarcode("");
    setVacutainerBarcode("");
    setSubjectStatus("idle");
    setVacutainerStatus("idle");
    setValidatedSubjectData(null);
    setIsCollectScanning(false);
    setActiveAliquotIndex(0);
    setAliquotStatus("idle");
    if (sessionData?.aliquotspersubject) {
      setAliquotBarcodes(Array(sessionData.aliquotspersubject).fill(""));
    }
    setFormMessage({ text: "", type: "" });
    scanLockRef.current = false;
    lastScannedRef.current = "";
    setProcessing(false);
    resetScanStartTime();
  };

  /** Handles secondary vacutainer re-scan submit in Auto Collection mode */
  const handleCollectScanSubmit = (scannedVacutainer: string) => {
    const validation = validateStep1Barcodes(subjectBarcode, scannedVacutainer);
    if (validation !== "success" && scannedVacutainer.trim() !== vacutainerBarcode.trim()) {
      setFormMessage({ text: "Invalid or Mismatched Vacutainer Barcode", type: "error" });
      playErrorBeep();
      speak("Error. Invalid vacutainer barcode.");
      setTimeout(() => setFormMessage({ text: "", type: "" }), 2500);
      return;
    }

    setProcessing(true);

    collectionService.InsertMutation.mutate(
      {
        sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
        subjectbarcode: subjectBarcode,
        vaccutainerbarcode: scannedVacutainer,
        result: "valid",
        resultmessage: "Match & Sample Collected Successfully",
        isverify: false,
        isinsert: true,
      },
      {
        onSuccess: () => {
          const autoPayload = {
            vaccutainerbarcode: scannedVacutainer,
            samplecollectiontime: getNow(),
            updatedby: Number(user?.userid || 1),
            updatereason: "Auto sample collection on vacutainer scan",
            studyid: sessionData?.studyid
          };

          CollectionTimeUpdateMutation.mutate(autoPayload, {
            onSuccess: (collRes: any) => {
              const successMsg = collRes?.message || "Sample collection recorded successfully!";
              setFormMessage({ text: successMsg, type: "success" });
              showToastMessage("success", successMsg);
              playSuccessBeep();
              speak("Sample collection recorded.");
              queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
              queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              setProcessing(false);
              setTimeout(() => {
                setIsCollectScanning(false);
                clearAndRestart();
              }, 1200);
            },
            onError: (err: any) => {
              const errMsg = typeof err === "string" ? err : err?.message || "Failed to update sample collection time.";
              setFormMessage({ text: errMsg, type: "error" });
              playErrorBeep();
              setProcessing(false);
              setTimeout(() => setFormMessage({ text: "", type: "" }), 2500);
            },
          });
        },
        onError: (err: any) => {
          const errorData = err?.response?.data || err;
          const errMsg = typeof errorData === "string" ? errorData : errorData?.message || "Failed to record scan";
          setFormMessage({ text: errMsg, type: "error" });
          playErrorBeep();
          setProcessing(false);
          setTimeout(() => setFormMessage({ text: "", type: "" }), 2500);
        },
      }
    );
  };

  /**
   * Validates Step 1 Subject to Vacutainer Barcodes client-side before submission.
   * Rules:
   * - Vacutainer barcode length must be 10 chars.
   * - Subject prefix (chars 0..5) must match Vacutainer prefix (chars 0..5).
   * - Period number (char 6) and Timepoint number (chars 7..8) must match active session.
   * - Last digit (char 9) must be '1' for vacutainer type.
   */
  const validateStep1Barcodes = (sub_barcode: string, vac_barcode: string) => {
    if (vac_barcode.length !== 10) return "vacutainer barcode length must be 10 characters";
    const sub_prefix = sub_barcode.substring(0, 6);
    const vac_prefix = vac_barcode.substring(0, 6);

    const period = parseInt(vac_barcode.substring(6, 7));
    const timepointBarcode = vac_barcode.substring(7, 9);
    const v_type = parseInt(vac_barcode.substring(9, 10));

    const timepointSession =
      sessionData?.timepoint_sort < 10
        ? `0${sessionData?.timepoint_sort}`
        : `${sessionData?.timepoint_sort}`;

    if (v_type !== 1) return "Not a vacutainer barcode";
    if (period !== sessionData?.period_sort) return "Invalid period";
    if (timepointBarcode !== timepointSession) return "Invalid timepoint";
    if (sub_prefix !== vac_prefix) return "Barcode Mismatch";

    return "success";
  };

  /**
   * Validates Step 2 Vacutainer to Aliquot Barcodes client-side.
   * Rules:
   * - Both barcodes must be 10 chars long.
   * - Vacutainer type must be 1, Aliquot type >= 2.
   * - Aliquot type digit must equal expected sequence tag (2 for 1st aliquot, 3 for 2nd, etc.).
   * - Base 9-char prefix must match between vacutainer and aliquot.
   */
  const validateStep2Barcode = (vac_barcode: string, ali_barcode: string, expected_tag: number) => {
    if (vac_barcode.length !== 10) return "Invalid vacutainer barcode";
    if (ali_barcode.length !== 10) return "Invalid aliquot barcode";

    const vac_type = parseInt(vac_barcode.substring(9, 10));
    const ali_type = parseInt(ali_barcode.substring(9, 10));
    const period = parseInt(ali_barcode.substring(6, 7));
    const timepointBarcode = ali_barcode.substring(7, 9);

    const vac_prefix = vac_barcode.substring(0, 9);
    const ali_prefix = ali_barcode.substring(0, 9);

    const timepointSession =
      sessionData?.timepoint_sort < 10
        ? `0${sessionData?.timepoint_sort}`
        : `${sessionData?.timepoint_sort}`;

    if (vac_type !== 1) return "Invalid vacutainer type";
    if (ali_type < 2) return "Not a valid aliquot barcode";
    if (period !== sessionData?.period_sort) return "Invalid period";
    if (timepointBarcode !== timepointSession) return "Invalid timepoint";
    if (ali_type !== expected_tag) return "Aliquot sequence mismatch";
    if (vac_prefix !== ali_prefix) return "Barcode Mismatch";

    return "success";
  };

  /**
   * Central barcode submit handler for camera scanner or manual keyboard entry.
   * Routes barcode payload based on current active tab and step state.
   */
  const handleBarcodeSubmit = (barcode: string) => {
    const value = barcode.trim();
    if (!value) return;

    // Scan cooldown & in-flight locks to avoid double processing
    if (scanLockRef.current || isSpeakingRef.current) return;
    if (isProcessingRef.current) return;
    if (formMessage.type === "success" && !isCollectScanning) return;
    if (activeTab === "step1" && vacutainerStatus === "matched" && !isCollectScanning) return;
    if (
      collectionService.InsertMutation.isPending ||
      separationService.InsertMutation.isPending ||
      CollectionTimeUpdateMutation.isPending
    ) return;
    if (lastScannedRef.current === value) return;

    scanLockRef.current = true;
    lastScannedRef.current = value;
    setTimeout(() => {
      scanLockRef.current = false;
      lastScannedRef.current = "";
    }, 2500);

    setFormMessage({ text: "", type: "" });

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 1: Subject -> Vacutainer Scanning Flow
    // ─────────────────────────────────────────────────────────────────────────
    if (activeTab === "step1") {
      if (isCollectScanning) {
        handleCollectScanSubmit(value);
        return;
      }

      if (subjectStatus === "idle") {
        // Scanning Subject Barcode
        if (value.length !== 10) {
          setSubjectStatus("mismatch");
          setFormMessage({ text: "Subject barcode length must be 10 characters", type: "error" });
          playErrorBeep();
          speak("Error. Subject barcode length is invalid.", () => {
            setSubjectBarcode("");
            setSubjectStatus("idle");
            setFormMessage({ text: "", type: "" });
          });
          return;
        }
        if (value.substring(6, 10) !== "0000") {
          setSubjectStatus("mismatch");
          setFormMessage({ text: "Invalid subject barcode", type: "error" });
          playErrorBeep();
          speak("Error. Invalid subject barcode", () => {
            setSubjectBarcode("");
            setSubjectStatus("idle");
            setFormMessage({ text: "", type: "" });
          });
          return;
        }

        setProcessing(true);
        collectionService.ValidateSubjectMutation.mutate(
          {
            sessionid: id ? parseInt(id) : sessionData?.sessionid,
            subjectbarcode: value,
          },
          {
            onSuccess: (res: any) => {
              setProcessing(false);
              const msg = res?.message || "Subject Scanned.";
              setSubjectBarcode(value);
              setSubjectStatus("matched");
              setValidatedSubjectData(res?.data || null);
              setVacutainerBarcode("");
              setVacutainerStatus("idle");
              setFormMessage({ text: msg, type: "success" });
              playSuccessBeep();
              speak(msg, () => {
                setFormMessage({ text: "", type: "" });
              });
              setTimeout(() => {
                setFormMessage({ text: "", type: "" });
              }, 1500);
            },
            onError: (err: any) => {
              setProcessing(false);
              const errorData = err?.response?.data || err;
              let backendMsg =
                typeof errorData === "string"
                  ? errorData
                  : errorData?.message ||
                  errorData?.resultmessage ||
                  errorData?.result ||
                  err?.message;

              const parsedMsg = Array.isArray(backendMsg)
                ? backendMsg.join(", ")
                : backendMsg;
              const displayMsg =
                typeof parsedMsg === "string" && parsedMsg
                  ? parsedMsg
                  : "Invalid subject barcode";

              setSubjectStatus("mismatch");
              setFormMessage({ text: displayMsg, type: "error" });
              playErrorBeep();
              speak(`Error. ${displayMsg}`, () => {
                setSubjectBarcode("");
                setSubjectStatus("idle");
                setFormMessage({ text: "", type: "" });
              });
            },
          }
        );
      } else {
        // Scanning Vacutainer Barcode for Subject
        const validation = validateStep1Barcodes(subjectBarcode, value);
        if (validation === "success") {
          const msg = "Match Verified.";
          setFormMessage({ text: msg, type: "success" });
          setVacutainerBarcode(value);
          setVacutainerStatus("matched");
          playSuccessBeep();
          speak("Barcode Matched.");
        } else {
          setVacutainerStatus("mismatch");
          setFormMessage({ text: validation, type: "error" });
          playErrorBeep();
          if (validation === "Barcode Mismatch") {
            setProcessing(true);
            collectionService.InsertMutation.mutate({
              sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
              subjectbarcode: subjectBarcode,
              vaccutainerbarcode: value,
              result: "invalid",
              resultmessage: "mismatched barcode",
            }, {
              onSuccess: () => {
                setProcessing(false);
                queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
                queryClient.invalidateQueries({ queryKey: ["sample-seperation-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["sample-seperation-scan-progress"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              },
              onError: () => {
                setProcessing(false);
              }
            });
          }
          speak(`Error. ${validation}`);
          setTimeout(() => {
            setVacutainerBarcode("");
            setVacutainerStatus("idle");
            setFormMessage({ text: "", type: "" });
          }, 2500);
        }
      }
    } else {
      // ───────────────────────────────────────────────────────────────────────
      // STEP 2: Vacutainer -> Aliquot Separation Flow
      // ───────────────────────────────────────────────────────────────────────
      if (vacutainerStatus === "idle") {
        // Scanning Vacutainer Barcode
        if (value.length !== 10) {
          setFormMessage({ text: "Invalid vacutainer barcode length", type: "error" });
          playErrorBeep();
          speak("Error. Invalid vacutainer barcode length.");
          setTimeout(() => {
            setVacutainerBarcode("");
            setFormMessage({ text: "", type: "" });
          }, 2500);
          return;
        }
        const vac_type = parseInt(value.substring(9, 10));
        const vac_period = parseInt(value.substring(6, 7));
        const vac_timepoint = value.substring(7, 9);
        const timepointSession =
          sessionData?.timepoint_sort < 10
            ? `0${sessionData?.timepoint_sort}`
            : `${sessionData?.timepoint_sort}`;

        if (vac_period !== sessionData?.period_sort || vac_timepoint !== timepointSession || vac_type !== 1) {
          let errorMsg = "Invalid vacutainer barcode parameter.";
          if (vac_type !== 1) errorMsg = "Invalid vacutainer type.";
          else if (vac_period !== sessionData?.period_sort) errorMsg = "Invalid period.";
          else if (vac_timepoint !== timepointSession) errorMsg = "Invalid timepoint.";
          setFormMessage({ text: errorMsg, type: "error" });
          playErrorBeep();
          speak(`Error. ${errorMsg}`);
          setTimeout(() => {
            setVacutainerBarcode("");
            setFormMessage({ text: "", type: "" });
          }, 2500);
          return;
        }

        setProcessing(true);
        separationService.ValidateVaccutainerMutation.mutate(
          {
            sessionid: id ? parseInt(id) : sessionData?.sessionid,
            vaccutainerbarcode: value,
          },
          {
            onSuccess: (res: any) => {
              setProcessing(false);
              const msg = res?.message || "Vacutainer Scanned.";
              setVacutainerBarcode(value);
              setVacutainerStatus("matched");
              setActiveAliquotIndex(1);
              setFormMessage({ text: msg, type: "success" });
              playSuccessBeep();
              speak(msg, () => {
                setFormMessage({ text: "", type: "" });
              });
              setTimeout(() => {
                setFormMessage({ text: "", type: "" });
              }, 1500);
            },
            onError: (err: any) => {
              setProcessing(false);
              const errorData = err?.response?.data || err;
              let backendMsg =
                typeof errorData === "string"
                  ? errorData
                  : errorData?.message ||
                  errorData?.resultmessage ||
                  errorData?.result ||
                  err?.message;

              const parsedMsg = Array.isArray(backendMsg)
                ? backendMsg.join(", ")
                : backendMsg;
              const displayMsg =
                typeof parsedMsg === "string" && parsedMsg
                  ? parsedMsg
                  : "Invalid vacutainer barcode";

              setVacutainerStatus("mismatch");
              setFormMessage({ text: displayMsg, type: "error" });
              playErrorBeep();
              speak(`Error. ${displayMsg}`, () => {
                setVacutainerBarcode("");
                setVacutainerStatus("idle");
                setFormMessage({ text: "", type: "" });
              });
            },
          }
        );
      } else {
        // Scanning Aliquot barcode at activeAliquotIndex
        const expected_tag = activeAliquotIndex + 1;
        const validation = validateStep2Barcode(vacutainerBarcode, value, expected_tag);

        if (validation === "success") {
          const updated = [...aliquotBarcodes];
          updated[activeAliquotIndex - 1] = value;
          setAliquotBarcodes(updated);
          playSuccessBeep();

          const isLast = activeAliquotIndex === sessionData.aliquotspersubject;

          setProcessing(true);
          separationService.InsertMutation.mutate(
            {
              sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
              vaccutainerbarcode: vacutainerBarcode,
              aliquotbarcode: value,
              result: "valid",
              resultmessage: "Matched Successfully",
            },
            {
              onSuccess: (res: any) => {
                setProcessing(false);
                queryClient.invalidateQueries({ queryKey: ["sample-seperation-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["sample-seperation-scan-progress"] });
                queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-seperation-select-all"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });

                const msg = res?.message || (isLast ? "Match Verified." : `Aliquot ${activeAliquotIndex} matched.`);
                setFormMessage({ text: msg, type: "success" });

                if (isLast) {
                  speak("Barcode Matched.");
                  setTimeout(() => {
                    clearAndRestart();
                  }, 1200);
                } else {
                  const nextIdx = activeAliquotIndex + 1;
                  setActiveAliquotIndex(nextIdx);
                  speak("Barcode Matched.");
                  setTimeout(() => {
                    setFormMessage({ text: "", type: "" });
                  }, 800);
                }
              },
              onError: (err: any) => {
                setProcessing(false);
                const errorData = err?.response?.data || err;
                const displayMsg = typeof errorData === "string" ? errorData : errorData?.message || err?.message || "Failed to record scan";
                setFormMessage({ text: displayMsg, type: "error" });
                playErrorBeep();
                speak(`Error. ${displayMsg}`);
                setTimeout(() => {
                  setAliquotStatus("idle");
                  setFormMessage({ text: "", type: "" });
                }, 2500);
              },
            }
          );
        } else {
          setAliquotStatus("mismatch");
          setFormMessage({ text: validation, type: "error" });
          playErrorBeep();
          if (validation === "Barcode Mismatch") {
            setProcessing(true);
            separationService.InsertMutation.mutate({
              sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
              vaccutainerbarcode: vacutainerBarcode,
              aliquotbarcode: value,
              result: "invalid",
              resultmessage: "mismatched barcode",
            }, {
              onSuccess: () => {
                setProcessing(false);
                queryClient.invalidateQueries({ queryKey: ["sample-seperation-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["sample-seperation-scan-progress"] });
                queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["sample-collection-scan-progress"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-seperation-select-all"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              },
              onError: () => {
                setProcessing(false);
              }
            });
          }
          speak(`Error. ${validation}`);
          setTimeout(() => {
            setAliquotStatus("idle");
            setFormMessage({ text: "", type: "" });
          }, 2500);
        }
      }
    }
  };

  const handleBarcodeSubmitRef = useRef<any>(null);
  useEffect(() => {
    handleBarcodeSubmitRef.current = handleBarcodeSubmit;
  });

  const [torchOn, setTorchOn] = useState(false);

  const customStreamRef = useRef<MediaStream | null>(null);
  const detectionIntervalRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoElementState, setVideoElementState] = useState<HTMLVideoElement | null>(null);

  const setVideoRef = React.useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoElementState(node);
  }, []);

  const initSeqRef = useRef(0);
  const pendingInitPromiseRef = useRef<Promise<void> | null>(null);
  const detectorRef = useRef<any>(null);

  /** Helper to asynchronously load external JS scripts (e.g. BarcodeDetector polyfill) with global caching */
  const scriptCache = useRef<Record<string, Promise<void>>>({});
  const loadScript = (src: string): Promise<void> => {
    if (document.querySelector(`script[src="${src}"]`)) {
      return Promise.resolve();
    }
    if (!scriptCache.current[src]) {
      scriptCache.current[src] = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          delete scriptCache.current[src];
          reject(new Error(`Failed to load script ${src}`));
        };
        document.body.appendChild(script);
      });
    }
    return scriptCache.current[src];
  };

  const isScannerHidden = activeTab === "step1" && vacutainerStatus === "matched" && !isCollectScanning;

  /**
   * Initializes real-time camera stream & continuous BarcodeDetector processing loop.
   */
  useEffect(() => {
    let active = true;

    let permissionStatus: PermissionStatus | null = null;
    const handlePermissionChange = () => {
      if (active && permissionStatus?.state === "granted") {
        initEngine();
      }
    };

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "camera" as any }).then((status) => {
        if (!active) return;
        permissionStatus = status;
        status.addEventListener("change", handlePermissionChange);
      }).catch(() => { });
    }

    const cleanupActiveScanners = async (stopStream = true) => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
      if (stopStream && customStreamRef.current) {
        try {
          customStreamRef.current.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping custom stream tracks", e);
        }
        customStreamRef.current = null;
      }
      if (videoRef.current && stopStream) {
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject = null;
        }
      }
    };

    const initEngine = async () => {
      const seq = ++initSeqRef.current;

      if (!activeTab || activeTab === "select" || isShowingAllScans) {
        await cleanupActiveScanners(true);
        return;
      }

      if (isScannerHidden) {
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
          detectionIntervalRef.current = null;
        }
        return;
      }

      // If a previous initEngine execution is in progress, queue behind it instead of dropping execution
      if (pendingInitPromiseRef.current) {
        try {
          await pendingInitPromiseRef.current;
        } catch (e) {
          // ignore error from previous run
        }
      }

      if (seq !== initSeqRef.current || !active) {
        return;
      }

      const doInit = async () => {
        await cleanupActiveScanners(false);

        if (!videoRef.current) {
          return;
        }

        const videoElement = videoRef.current;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          const isSecure =
            window.location.protocol === "https:" ||
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";
          if (!isSecure) {
            showToastMessage("error", "Camera blocked: Mobile devices require a secure HTTPS connection or localhost to access the camera.");
          } else {
            showToastMessage("error", "Camera API is not supported in this browser environment.");
          }
          return;
        }

        resetScanStartTime();

        try {
          if (!detectorRef.current) {
            if (!("BarcodeDetector" in window)) {
              await loadScript("https://cdn.jsdelivr.net/npm/barcode-detector@3/dist/iife/index.min.js");
              if (seq !== initSeqRef.current || !active) return;
            }

            const BarcodeDetectorClass = (window as any).BarcodeDetector;
            if (!BarcodeDetectorClass) {
              throw new Error("BarcodeDetector API polyfill failed to load");
            }

            let detectorFormats = ["code_128", "code_39", "itf", "qr"];
            if (typeof BarcodeDetectorClass.getSupportedFormats === "function") {
              try {
                const supported = await BarcodeDetectorClass.getSupportedFormats();
                detectorFormats = detectorFormats.filter((f) => supported.includes(f));
              } catch (e) {
                console.error("Failed to fetch supported formats:", e);
              }
            }

            detectorRef.current = new BarcodeDetectorClass({
              formats: detectorFormats.length > 0 ? detectorFormats : ["qr"],
            });
          }
          const detector = detectorRef.current;

          let stream = customStreamRef.current;
          const isStreamLive = stream && stream.getVideoTracks().some((t) => t.readyState === "live");

          if (!isStreamLive) {
            let attempts = 0;
            const maxAttempts = 3;

            while (attempts < maxAttempts && active && seq === initSeqRef.current) {
              try {
                stream = await navigator.mediaDevices.getUserMedia({
                  video: { facingMode: { ideal: "environment" } },
                  audio: false,
                });
                break;
              } catch (err: any) {
                console.warn(`[Camera Engine] getUserMedia attempt ${attempts + 1} failed:`, err?.name || err);
                if (err?.name === "NotReadableError" || err?.name === "AbortError") {
                  attempts++;
                  if (attempts < maxAttempts && active && seq === initSeqRef.current) {
                    await new Promise((resolve) => setTimeout(resolve, attempts * 250));
                    continue;
                  }
                }
                throw err;
              }
            }

            if (!active || seq !== initSeqRef.current || !stream) {
              if (stream) {
                if (seq === initSeqRef.current) {
                  stream.getTracks().forEach((track) => track.stop());
                } else {
                  customStreamRef.current = stream;
                }
              }
              return;
            }
            customStreamRef.current = stream;
          }

          if (videoElement.srcObject !== stream) {
            videoElement.srcObject = stream;
          }
          videoElement.setAttribute("playsinline", "true");
          await videoElement.play().catch((err) => {
            console.warn("[Camera Engine] videoElement.play() warning:", err);
          });

          let isDetecting = false;
          const detectFrame = async () => {
            if (!active || isDetecting || isProcessingRef.current || scanLockRef.current || isSpeakingRef.current) return;
            if (isScannerHidden) return;
            isDetecting = true;
            try {
              if (videoElement && videoElement.readyState >= 2) {
                const barcodes = await detector.detect(videoElement);
                if (barcodes && barcodes.length > 0 && active) {
                  const code = barcodes[0].rawValue;
                  if (code) {
                    handleBarcodeSubmitRef.current?.(code);
                  }
                }
              }
            } catch (e) {
              // frame not ready or video stopped
            } finally {
              isDetecting = false;
            }
          };

          if (detectionIntervalRef.current) {
            clearInterval(detectionIntervalRef.current);
          }
          detectionIntervalRef.current = setInterval(detectFrame, 150);
        } catch (err: any) {
          console.error("[Camera Engine] Failed to initialize Barcode Detector:", err);
          const isSecure =
            window.location.protocol === "https:" ||
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";
          if (err && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")) {
            showToastMessage("error", "Camera permission denied. Please reset permissions in your browser address bar/settings.");
          } else if (!isSecure) {
            showToastMessage("error", "Camera blocked: Mobile devices require a secure HTTPS connection or localhost to access the camera.");
          } else {
            showToastMessage("error", `Failed to start camera for barcode detector: ${err.message || err}`);
          }
        }
      };

      const initPromise = doInit();
      pendingInitPromiseRef.current = initPromise;
      try {
        await initPromise;
      } finally {
        if (pendingInitPromiseRef.current === initPromise) {
          pendingInitPromiseRef.current = null;
        }
      }
    };

    initEngine();

    return () => {
      active = false;
      if (permissionStatus) {
        permissionStatus.removeEventListener("change", handlePermissionChange);
      }
    };
  }, [activeTab, isShowingAllScans, isScannerHidden, videoElementState]);

  /** Dedicated unmount effect to guarantee physical camera hardware release on page exit */
  useEffect(() => {
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
      if (customStreamRef.current) {
        try {
          customStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch (e) {
          console.error("Error stopping stream on unmount", e);
        }
        customStreamRef.current = null;
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  /** Toggles camera torch / flashlight on supporting devices */
  const toggleTorch = async () => {
    try {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject as MediaStream | null;
        const track = stream?.getVideoTracks()?.[0];
        if (track) {
          const capabilities = track.getCapabilities() as any;
          if (capabilities.torch) {
            const nextTorchState = !torchOn;
            await track.applyConstraints({
              advanced: [{ torch: nextTorchState } as any],
            });
            setTorchOn(nextTorchState);
          } else {
            showToastMessage("error", "Torch is not supported on this device/camera.");
          }
        }
      }
    } catch (err) {
      console.error("Failed to toggle torch", err);
    }
  };

  // Render Full Page Loader while initial session details load
  if (activeSessionQuery.isPending) {
    return <FullPageLoader />;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // DESKTOP LAYOUT (> 1024px)
  // ───────────────────────────────────────────────────────────────────────────
  if (isDesktop) {
    return (
      <React.Fragment>
        <div className={`page-content ${ac.pageContent}`}>
          {UpdateMutation.isPending && <FullPageLoader />}
          <Container fluid className="pt-0">
            {/* Session Page Header */}
            <div className={ac.pageHeader}>
              <div className="d-flex align-items-center gap-3">
                <Button
                  color="light"
                  outline
                  className="px-2 py-1 bg-white border"
                  onClick={handleBackClick}
                  disabled={UpdateMutation.isPending}
                >
                  <i className="ri-arrow-left-line text-dark"></i>
                </Button>
                <div>
                  <h5 className={ac.sessionTitle}>
                    Active Session
                    <span className={`badge bg-light text-secondary border fw-normal ms-2 ${ac.sessionBadge}`}>
                      {sessionData?.sessionno || "SESS-0091"}
                    </span>
                  </h5>
                  <p className={ac.sessionSubtitle}>
                    Execute and monitor the accessioning workflow for the current study period.
                  </p>
                </div>
              </div>
              <div className="d-flex gap-3 align-items-center">
                {isSessionCompleted ? (
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 fw-bold text-uppercase" style={{ fontSize: "12px" }}>
                    <i className="ri-checkbox-circle-fill me-1 align-middle fs-14" />
                    Session Completed
                  </span>
                ) : (
                  <>
                    {sessionData?.display_status === "PAUSED" ? (
                      <CommonButton
                        module={PERMISSION_MODULES.SESSION}
                        action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                        color="success"
                        outline
                        onClick={() => handleUpdateStatus("active", false)}
                        disabled={UpdateMutation.isPending}
                      >
                        Resume Session
                      </CommonButton>
                    ) : (
                      <CommonButton
                        module={PERMISSION_MODULES.SESSION}
                        action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                        color="secondary"
                        outline
                        onClick={() => handleUpdateStatus("paused", true)}
                        disabled={UpdateMutation.isPending}
                      >
                        Pause Session
                      </CommonButton>
                    )}
                    <CommonButton
                      module={PERMISSION_MODULES.SESSION}
                      action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                      color="primary"
                      outline
                      onClick={() => handleUpdateStatus("completed", true)}
                      disabled={UpdateMutation.isPending || !isSessionCompletedReady}
                    >
                      Complete Session
                    </CommonButton>
                  </>
                )}
              </div>
            </div>

            {/* Desktop Main Workspace Panel */}
            <div className="card shadow-sm border-0 mb-3">
              {/* Study Metadata Header Bar */}
              <div className="px-4 pt-3 pb-2 border-bottom">
                <div className="d-flex justify-content-between align-items-end">
                  <div className={`d-flex align-items-center gap-4 ${ac.metaRow}`}>
                    <div>
                      <i className="ri-folder-line me-1 text-primary"></i> Study:
                      <span className="fw-bold text-dark ms-1">{sessionData?.studycode}</span>
                    </div>
                    <div>
                      <i className="ri-calendar-line me-1 text-primary"></i> Period:
                      <span className="fw-bold text-dark ms-1">{sessionData?.period_number}</span>
                    </div>
                    <div>
                      <i className="ri-time-line me-1 text-primary"></i> Time Point:{" "}
                      <span className="fw-bold text-dark ms-1">{sessionData?.timepoint_number}</span>
                    </div>
                    {sessionData?.dosingstationno != null && (
                      <div>
                        <i className="ri-building-2-line me-1 text-primary"></i> Dosing Station:{" "}
                        <span className="fw-bold text-dark ms-1">
                          {`DS-${String(sessionData.dosingstationno).padStart(2, "0")}`}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={ac.progressWrapper} style={{ width: "260px" }}>
                    <div className={`d-flex justify-content-between text-muted mb-1 ${ac.progressLabel}`}>
                      <span>Session Progress</span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold text-success" title="Completed">
                          {completedSubs}
                        </span>
                        <span className="text-muted">/</span>
                        <span className="fw-bold text-info" title="In Progress">
                          {inProgressSubs}
                        </span>
                        <span className="text-muted">/</span>
                        <span className="fw-bold text-dark" title="Total Subjects">
                          {totalSubs}
                        </span>
                        <i
                          className="ri-eye-line text-primary fs-14 ms-1"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowProgressModal(true)}
                          title="View Detailed Progress"
                        />
                      </div>
                    </div>
                    <Progress multi className={ac.progressBar}>
                      <Progress bar value={(completedSubs / (totalSubs || 1)) * 100} color="success" />
                      <Progress bar value={(inProgressSubs / (totalSubs || 1)) * 100} color="info" />
                    </Progress>
                  </div>
                </div>
              </div>

              {/* Navigation Tabs Bar */}
              <div className={tabStyles.tabContainer}>
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}
                  type="button"
                  className={`${tabStyles.tabBtn} ${activeTab === "step1" ? tabStyles.active : ""}`}
                  onClick={() => toggleTab("step1")}
                >
                  Step 1: Subject to Vacutainer
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}
                  type="button"
                  className={`${tabStyles.tabBtn} ${activeTab === "step2" ? tabStyles.active : ""}`}
                  onClick={() => toggleTab("step2")}
                >
                  Step 2: Vacutainer to Aliquots
                </CommonButton>
              </div>

              {/* Step Sub-Component Workspace */}
              <div>
                {activeTab === "step1" ? (
                  <RouteGuard
                    module={PERMISSION_MODULES.SESSION}
                    action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}
                  >
                    <SubjectToVacutainer
                      sessionData={sessionData}
                      sessionid={id}
                      isParentModalOpen={showProgressModal}
                    />
                  </RouteGuard>
                ) : (
                  <RouteGuard
                    module={PERMISSION_MODULES.SESSION}
                    action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}
                  >
                    <VacutainerToAliquots
                      sessionData={sessionData}
                      sessionid={id}
                      isParentModalOpen={showProgressModal}
                    />
                  </RouteGuard>
                )}
              </div>
            </div>
          </Container>
        </div>

        {/* Detailed Progress Modal */}
        <ScanningSessionProgressModal
          isOpen={showProgressModal}
          toggle={() => setShowProgressModal(!showProgressModal)}
          sessionData={sessionData}
          activeTab={activeTab}
        />
      </React.Fragment>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // MOBILE / TABLET LAYOUT (<= 1024px)
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className={resStyles.pageContent}>
        {UpdateMutation.isPending && <FullPageLoader />}

        <div className={resStyles.mobileContainer}>
          {isShowingAllScans ? (
            <MobileScansOverlay
              historyScans={historyScans}
              activeTab={activeTab}
              setIsShowingAllScans={setIsShowingAllScans}
              totalrecords={overlayTotalCount}
              currentPage={overlayCurrentPage}
              onPageChange={(page) => setOverlayCurrentPage(page)}
              searchText={overlaySearchText}
              onSearchChange={(text) => {
                setOverlaySearchText(text);
                setOverlayCurrentPage(1);
              }}
            />
          ) : (
            <>
              {/* Mobile Header Bar */}
              <MobileHeader
                sessionData={sessionData}
                activeTab={activeTab}
                toggleTab={toggleTab}
                handleUpdateStatus={handleUpdateStatus}
                setShowProgressModal={setShowProgressModal}
                completedSubsCount={completedSubs}
                inProgressSubsCount={inProgressSubs}
                onBackClick={handleBackClick}
              />

              {/* Active Scanner View Card (Step 1 vs Step 2) */}
              <div className={resStyles.cardsContainer}>
                {activeTab === "step1" ? (
                  <RouteGuard
                    module={PERMISSION_MODULES.SESSION}
                    action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}
                  >
                    <MobileStep1Scanner
                      videoRef={setVideoRef as any}
                      torchOn={torchOn}
                      toggleTorch={toggleTorch}
                      subjectBarcode={subjectBarcode}
                      subjectStatus={subjectStatus}
                      vacutainerBarcode={vacutainerBarcode}
                      vacutainerStatus={vacutainerStatus}
                      handleBarcodeSubmit={handleBarcodeSubmit}
                      clearAndRestart={clearAndRestart}
                      formMessage={formMessage}
                      completedSubs={completedSubs + inProgressSubs}
                      totalSubs={totalSubs}
                      skippedSubs={sessionData?.skipped_subjects || 0}
                      onCollectClick={handleCollectClick}
                      isCollectScanning={isCollectScanning}
                      onSkipClick={handleSkipSubject}
                      isSkipEnabled={subjectStatus === "matched" || Boolean(subjectBarcode)}
                      sessionData={sessionData}
                      isProcessing={isProcessing}
                      validatedSubjectData={validatedSubjectData}
                    />
                  </RouteGuard>
                ) : (
                  <RouteGuard
                    module={PERMISSION_MODULES.SESSION}
                    action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}
                  >
                    <MobileStep2Scanner
                      videoRef={setVideoRef as any}
                      torchOn={torchOn}
                      toggleTorch={toggleTorch}
                      vacutainerBarcode={vacutainerBarcode}
                      vacutainerStatus={vacutainerStatus}
                      activeAliquotIndex={activeAliquotIndex}
                      totalAliquots={sessionData?.aliquotspersubject}
                      aliquotBarcodes={aliquotBarcodes}
                      handleBarcodeSubmit={handleBarcodeSubmit}
                      clearAndRestart={clearAndRestart}
                      formMessage={formMessage}
                      completedSubs={completedSubs}
                      totalSubs={totalSubs}
                      sessionData={sessionData}
                      isProcessing={isProcessing}
                    />
                  </RouteGuard>
                )}
              </div>

              {/* Mini Recent Scan Logs (Last 3) */}
              <div className={resStyles.recentScansMiniList}>
                <div className={resStyles.miniListHeader}>
                  <h6>Recent Scans</h6>
                  <button type="button" onClick={() => setIsShowingAllScans(true)}>
                    View All
                  </button>
                </div>
                <div className={resStyles.miniListItems}>
                  {historyScans.slice(0, 3).map((row: any, idx: number) => {
                    const resultStatus = (row.result || "valid").toLowerCase();

                    return (
                      <div key={idx} className={resStyles.miniListItem}>
                        <div
                          className={resStyles.miniItemLeft}
                          style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span className={resStyles.miniItemTime} style={{ minWidth: "60px", display: "inline-block" }}>
                              {row.scandate
                                ? new Date(row.scandate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                : "-"}
                            </span>
                            <span className={resStyles.miniItemValues}>
                              {activeTab === "step1"
                                ? `${row.subjectbarcode || "-"} → ${row.vaccutainerbarcode || "-"}`
                                : `${row.vaccutainerbarcode || "-"} → ${row.aliquotbarcode || "-"}`}
                            </span>
                          </div>
                          {activeTab === "step1" && (row.dosingscheduletime || row.samplecollectiontime) && (
                            <span style={{ fontSize: "11px", color: "#64748b", paddingLeft: "70px", fontWeight: "normal" }}>
                              (Sch: {row.dosingscheduletime ? row.dosingscheduletime.substring(0, 5) : "-"} | Coll:{" "}
                              {row.samplecollectiontime ? row.samplecollectiontime.substring(0, 5) : "-"})
                            </span>
                          )}
                        </div>
                        {resultStatus === "skipped" ? (
                          <span
                            className="badge bg-warning bg-opacity-10 border border-warning border-opacity-50 px-2 py-1 text-uppercase fw-bold shadow-sm"
                            style={{
                              fontSize: "11px",
                              backgroundColor: "#fff8e6",
                              color: "#b45309",
                              borderColor: "#f59e0b",
                              borderRadius: "6px",
                              padding: "3px 8px",
                            }}
                          >
                            SKIPPED
                          </span>
                        ) : resultStatus === "valid" ? (
                          <span
                            className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1 text-uppercase fw-bold d-inline-flex align-items-center gap-1"
                            style={{
                              fontSize: "11px",
                              backgroundColor: "#dcfce7",
                              color: "#15803d",
                              borderColor: "#86efac",
                              borderRadius: "6px",
                              padding: "3px 8px",
                            }}
                          >
                            VALID
                          </span>
                        ) : (
                          <span
                            className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-2 py-1 text-uppercase fw-bold d-inline-flex align-items-center gap-1"
                            style={{
                              fontSize: "11px",
                              borderRadius: "6px",
                              padding: "3px 8px",
                            }}
                          >
                            {row.result || "INVALID"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {historyScans.length === 0 && (
                    <div className={resStyles.miniListEmpty}>No scans recorded yet.</div>
                  )}
                </div>
              </div>

              {/* Bottom Action Stack */}
              <div className={resStyles.bottomActionsStack}>
                {!isSessionCompleted && (
                  <>
                    <button
                      type="button"
                      className={resStyles.bottomActionBtn}
                      onClick={() => handleUpdateStatus("completed", true)}
                      disabled={UpdateMutation.isPending || !isSessionCompletedReady}
                      style={{
                        backgroundColor: isSessionCompletedReady ? "#10b981" : undefined,
                        color: isSessionCompletedReady ? "#ffffff" : undefined,
                        borderColor: isSessionCompletedReady ? "#10b981" : undefined,
                        opacity: UpdateMutation.isPending || !isSessionCompletedReady ? 0.6 : 1,
                        cursor: UpdateMutation.isPending || !isSessionCompletedReady ? "not-allowed" : "pointer",
                      }}
                    >
                      <i className="ri-checkbox-circle-line"></i> Complete Session
                    </button>
                    <button
                      type="button"
                      className={resStyles.bottomActionBtn}
                      onClick={() => handleUpdateStatus("paused", true)}
                    >
                      <i className="ri-pause-line"></i> Pause Session
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className={resStyles.bottomActionBtn}
                  onClick={() => setIsShowingAllScans(true)}
                >
                  <i className="ri-eye-line"></i> View All Scans
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Progress View Modal */}
      <ScanningSessionProgressModal
        isOpen={showProgressModal}
        toggle={() => setShowProgressModal(!showProgressModal)}
        sessionData={sessionData}
        activeTab={activeTab}
      />

      {/* Manual Sample Collection Time Confirm Modal */}
      <Modal isOpen={showCollectModal} toggle={() => setShowCollectModal(false)} centered className="common_modal" size="sm">
        <ModalHeader toggle={() => setShowCollectModal(false)} className="fw-bold border-bottom py-2 px-3">
          <i className="ri-drop-line me-2 text-primary fs-18 align-middle" />
          <span className="align-middle fs-16">Confirm Collection</span>
        </ModalHeader>
        <ModalBody className="py-3 px-3 text-center">
          <div className="mb-2">
            <div
              style={{
                backgroundColor: "#e6fffa",
                color: "#0d9488",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              <i className="ri-checkbox-circle-fill"></i>
            </div>
          </div>
          <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "14px" }}>
            Record Collection Time
          </h6>
          <p className="text-muted mb-3" style={{ fontSize: "12px", lineHeight: "1.4" }}>
            Confirm sample collection for Subject <strong>{subjectBarcode}</strong>?
          </p>
          <div
            className="d-flex align-items-center justify-content-center gap-2 py-1 px-3 rounded mb-1"
            style={{ backgroundColor: "#f8fafc", border: "1px dashed #cbd5e1", display: "inline-flex", minWidth: "150px" }}
          >
            <i className="ri-time-line text-secondary fs-16" />
            <span className="fw-bold text-dark fs-14">{collectTime}</span>
          </div>
        </ModalBody>
        <ModalFooter className="bg-white border-top py-2 px-3 d-flex justify-content-end gap-2">
          <Button
            size="sm"
            color="dark"
            outline
            onClick={() => setShowCollectModal(false)}
            disabled={collectionService.InsertMutation.isPending || CollectionTimeUpdateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="teal"
            style={{ backgroundColor: "#0d9488", borderColor: "#0d9488", color: "#fff" }}
            onClick={handleConfirmCollect}
            disabled={collectionService.InsertMutation.isPending || CollectionTimeUpdateMutation.isPending}
          >
            {collectionService.InsertMutation.isPending || CollectionTimeUpdateMutation.isPending ? "Saving..." : "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Skip Subject Reason Dialog */}
      <SkipReasonModal
        isOpen={showSkipModal}
        toggle={() => setShowSkipModal(false)}
        subjectBarcode={subjectBarcode}
        onSubmit={handleConfirmSkipSubject}
        isPending={collectionService.InsertMutation.isPending}
      />
    </React.Fragment>
  );
};

export default ScanningSessionPage;
